import { type Difficulty, isDifficulty } from "./ai";
import { type ArcadeId, type ArcadeView, isArcadeId } from "./arcade";
import type { InputEvent } from "./realtime";
import type { GameState } from "./registry";
import { type GameId, isGameId, type PlayerIndex } from "./types";

export const ROOM_CODE_LENGTH = 4;
/**
 * Consonants only: no 0/O or 1/I to squint at, and no accidental rude words.
 */
export const ROOM_CODE_ALPHABET = "BCDFGHJKLMNPQRSTVWXZ";

/** Fixed lists, so a name is always a pick — never something a kid typed. */
export const NAME_ADJECTIVES = [
	"Brave",
	"Cheerful",
	"Clever",
	"Cosmic",
	"Daring",
	"Speedy",
	"Sunny",
	"Turbo",
	"Wild",
	"Zippy",
] as const;

export const NAME_ANIMALS = [
	"Otter",
	"Panda",
	"Falcon",
	"Tiger",
	"Dolphin",
	"Badger",
	"Fox",
	"Yak",
	"Koala",
	"Hedgehog",
] as const;

export const AVATARS = [
	"🦊",
	"🐼",
	"🐨",
	"🦁",
	"🐸",
	"🐙",
	"🦄",
	"🐧",
	"🦖",
	"🐳",
	"🦉",
	"🐝",
] as const;

export function normalizeRoomCode(raw: string): string {
	return raw
		.toUpperCase()
		.split("")
		.filter((char) => ROOM_CODE_ALPHABET.includes(char))
		.join("")
		.slice(0, ROOM_CODE_LENGTH);
}

export function isRoomCode(value: unknown): value is string {
	return (
		typeof value === "string" &&
		value.length === ROOM_CODE_LENGTH &&
		normalizeRoomCode(value) === value
	);
}

export function isPlayerName(value: unknown): value is string {
	if (typeof value !== "string") return false;
	const [adjective, animal, ...rest] = value.split(" ");
	if (rest.length > 0) return false;
	return (
		(NAME_ADJECTIVES as readonly string[]).includes(adjective ?? "") &&
		(NAME_ANIMALS as readonly string[]).includes(animal ?? "")
	);
}

export function isAvatar(value: unknown): value is string {
	return typeof value === "string" && (AVATARS as readonly string[]).includes(value);
}

/** Taps, not typing: reacting never becomes a way to say something unkind. */
export const REACTIONS = ["👍", "😂", "😮", "🎉", "🤝", "🔥"] as const;
export type Reaction = (typeof REACTIONS)[number];

export function isReaction(value: unknown): value is Reaction {
	return typeof value === "string" && (REACTIONS as readonly string[]).includes(value);
}

/** Play a friend, or play the computer. */
export type RoomMode = "friend" | "computer";

export const BEST_OF_OPTIONS = [1, 3, 5] as const;
export type BestOf = (typeof BEST_OF_OPTIONS)[number];

export function isBestOf(value: unknown): value is BestOf {
	return value === 1 || value === 3 || value === 5;
}

/** Turn clock choices, in seconds. `0` turns it off. */
export const TURN_SECONDS_OPTIONS = [0, 20, 45] as const;
export type TurnSeconds = (typeof TURN_SECONDS_OPTIONS)[number];

export function isTurnSeconds(value: unknown): value is TurnSeconds {
	return value === 0 || value === 20 || value === 45;
}

/** Games needed to take the match. */
export function gamesToWin(bestOf: BestOf): number {
	return Math.ceil(bestOf / 2);
}

export interface MatchView {
	bestOf: BestOf;
	/** Games won so far this match. */
	wins: [number, number];
	/** 1-based number of the game being played. */
	gameNumber: number;
	/** Set once someone has taken the match. */
	winner: PlayerIndex | null;
}

export interface PlayerView {
	name: string;
	avatar: string;
	connected: boolean;
	/** Rounds won in this room so far. */
	wins: number;
	/** The computer opponent, rather than a person. */
	isBot: boolean;
	/** Their profile code, when they are signed in. */
	playerCode: string | null;
}

export interface RoomView {
	code: string;
	/** `lobby` while a game is being chosen, `playing` once one is running. */
	phase: "lobby" | "playing";
	players: [PlayerView | null, PlayerView | null];
	gameId: GameId | null;
	/** The arcade run in progress, or null when an ordinary game is on. */
	arcade: ArcadeView | null;
	state: GameState | null;
	/** Seat that gets to choose the game. */
	hostSeat: PlayerIndex;
	/** The server's clock, so countdowns don't drift with the device clock. */
	serverNow: number;
	mode: RoomMode;
	/** How hard the computer plays, in a `computer` room. */
	difficulty: Difficulty | null;
	/** The running series, once a game has been picked. */
	match: MatchView | null;
	/** Turn clock length; `0` when it is switched off. */
	turnSeconds: TurnSeconds;
	/** When the current turn runs out, if the clock is on. */
	turnDeadline: number | null;
	/**
	 * When this viewer may claim the win because the other player has gone, or
	 * null whenever that does not apply — nobody is missing, no game is running,
	 * or it is this viewer who is away.
	 *
	 * The win is offered, never applied on its own. Losing your connection should
	 * not lose you the game, but it should not strand the other player either.
	 */
	claimableAt: number | null;
}

/** How long someone has to come back before the other player may claim the win. */
export const CLAIM_GRACE_MS = 45_000;

/** When a seat that went away at `awaySince` becomes claimable against. */
export function claimableFrom(awaySince: number | null): number | null {
	return awaySince === null ? null : awaySince + CLAIM_GRACE_MS;
}

/**
 * Whether the offer is open yet. The server decides this before awarding the
 * game, and the client uses the same rule to decide when to show the button, so
 * the two can never disagree about whether the wait is over.
 */
export function canClaim(claimableAt: number | null, now: number): boolean {
	return claimableAt !== null && now >= claimableAt;
}

export interface PickOptions {
	gameId: GameId;
	bestOf: BestOf;
	turnSeconds: TurnSeconds;
}

export type ClientMessage =
	| {
			type: "join";
			playerId: string;
			name: string;
			avatar: string;
			/** A signed-in player's session token, so results count towards stats. */
			token: string | null;
	  }
	| ({ type: "pick" } & PickOptions)
	| { type: "move"; move: unknown }
	| { type: "react"; emoji: Reaction }
	| { type: "again" }
	| { type: "lobby" }
	/** Start an arcade run: the server picks the seed both players will use. */
	| { type: "arcade"; arcadeId: ArcadeId }
	/**
	 * A finished run, as the presses that produced it. Never a score — the
	 * server replays this and works the score out itself.
	 */
	| { type: "run"; log: InputEvent<unknown>[] }
	/** Change how hard the computer plays, without leaving the room. */
	| { type: "difficulty"; difficulty: Difficulty }
	| { type: "claim" }
	| { type: "ping" };

export type ServerErrorCode = "room-not-found" | "room-full" | "bad-message" | "rejected";

export type ServerMessage =
	| { type: "welcome"; seat: PlayerIndex; room: RoomView }
	| { type: "room"; room: RoomView }
	| { type: "reaction"; seat: PlayerIndex; emoji: Reaction; at: number }
	| { type: "pong" }
	| { type: "error"; code: ServerErrorCode; message: string };

/**
 * Why the server hung up: this seat has been taken over from somewhere else.
 *
 * A normal 1000 is indistinguishable from a dropped connection, and the client
 * reconnects after any close — so two tabs holding the same seat would take it
 * from each other for ever, several times a second, burning the per-address
 * socket budget while showing a permanent "reconnecting" banner in both. 4000
 * upwards is the range reserved for applications to say something like this.
 */
export const CLOSE_TAKEN_OVER = 4001;

/**
 * The most presses any arcade run may carry.
 *
 * One press per step is the most a player can produce, and the longest run any
 * game allows is a few thousand steps — so this is comfortably above anything
 * real and far below what a hostile client would like to send.
 */
export const MAX_RUN_PRESSES = 12_000;

/**
 * The longest `join.token` accepted off the wire.
 *
 * A real session token is 43 characters — 32 random bytes, base64url — so this
 * is comfortably above anything the server has ever minted, and far below the
 * megabytes a hostile client could otherwise attach to every join. It was the
 * one string off the wire with no bound of its own.
 */
export const MAX_TOKEN_CHARS = 512;

/** Sent verbatim so the server can answer it without waking the room up. */
export const PING_FRAME = JSON.stringify({ type: "ping" });
export const PONG_FRAME = JSON.stringify({ type: "pong" });

/** Player ids are opaque, generated by the browser and kept in localStorage. */
export function isPlayerId(value: unknown): value is string {
	return typeof value === "string" && value.length >= 8 && value.length <= 64;
}

/** Validates a message straight off the socket. Returns `null` if it is junk. */
export function parseClientMessage(raw: unknown): ClientMessage | null {
	if (typeof raw !== "object" || raw === null) return null;
	const message = raw as Record<string, unknown>;

	switch (message.type) {
		case "join": {
			if (!isPlayerId(message.playerId)) return null;
			if (!isPlayerName(message.name) || !isAvatar(message.avatar)) return null;
			const token = message.token;
			if (token !== undefined && token !== null && typeof token !== "string") return null;
			// Far longer than any token ever minted means it is junk, not a session.
			if (typeof token === "string" && token.length > MAX_TOKEN_CHARS) return null;
			return {
				type: "join",
				playerId: message.playerId,
				name: message.name,
				avatar: message.avatar,
				token: typeof token === "string" ? token : null,
			};
		}
		case "pick": {
			if (!isGameId(message.gameId)) return null;
			// Defaults keep older clients working: one game, no clock.
			const bestOf = message.bestOf ?? 1;
			const turnSeconds = message.turnSeconds ?? 0;
			if (!isBestOf(bestOf) || !isTurnSeconds(turnSeconds)) return null;
			return { type: "pick", gameId: message.gameId, bestOf, turnSeconds };
		}
		case "react":
			return isReaction(message.emoji) ? { type: "react", emoji: message.emoji } : null;
		case "move":
			return "move" in message ? { type: "move", move: message.move } : null;
		case "difficulty":
			return isDifficulty(message.difficulty)
				? { type: "difficulty", difficulty: message.difficulty }
				: null;
		case "arcade":
			return isArcadeId(message.arcadeId) ? { type: "arcade", arcadeId: message.arcadeId } : null;
		case "run": {
			// Shape only. Whether the presses make sense is `verifyRun`'s job, and
			// it has to assume they do not.
			//
			// The length cap is here rather than only in `verifyRun` so that a log
			// far too long to have been played is refused before it is copied out
			// entry by entry. Nothing legitimate comes close: the longest run any
			// arcade game allows is a few thousand steps, and one press per step
			// is the most a player can produce.
			if (!Array.isArray(message.log) || message.log.length > MAX_RUN_PRESSES) return null;
			const log: InputEvent<unknown>[] = [];
			for (const entry of message.log) {
				if (typeof entry !== "object" || entry === null) return null;
				const { at, input } = entry as { at?: unknown; input?: unknown };
				if (typeof at !== "number" || !Number.isInteger(at)) return null;
				if (input === undefined) return null;
				log.push({ at, input });
			}
			return { type: "run", log };
		}
		case "again":
			return { type: "again" };
		case "lobby":
			return { type: "lobby" };
		case "claim":
			return { type: "claim" };
		case "ping":
			return { type: "ping" };
		default:
			return null;
	}
}
